// How big a picture is, and where it sits on the canvas.
//
// A vector (SVG) has no pixels of its own: it is drawn at whatever size the
// browser lays it out at. Scaling one with a CSS transform therefore stretches
// a rasterisation made at its intrinsic size - exactly the blur the product
// owner saw. Giving the element a real width and height instead makes the
// browser re-draw the vector at display size, so it stays sharp at any zoom.
//
// The maths lives here, apart from the component, so it can be tested on its
// own and reasoned about without a DOM.

// A vector is never pinned to 2x: a drawing exported at 300px wide should fill
// the canvas, because filling it costs nothing in quality. A bitmap keeps the
// old cap, where enlarging past 2x only enlarges its pixels.
export const MIN_SCALE = 0.1;
export const MAX_SCALE = 8;
export const RASTER_FIT_MAX = 2;
// A vector with no size of its own at all (no width, no height, no viewBox,
// and a browser that reports nothing): a sheet of paper, so something sensible
// is shown rather than the browser's 300x150 default.
export const DEFAULT_VECTOR_SIZE = Object.freeze({ width: 1024, height: 768 });

export const clampScale = (k) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, k));

const openingTag = (markup) => {
  const match = /<svg\b[^>]*>/i.exec(markup || "");
  return match ? match[0] : "";
};

const attribute = (tag, name) => {
  const match = new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)')`, "i").exec(tag);
  if (!match) return "";
  return (match[2] !== undefined ? match[2] : match[3]) || "";
};

// "620", "620px", "620.5pt" - a length in CSS pixels, near enough for fitting.
// A percentage ("100%") says nothing about the drawing's own size, so it is
// ignored and the viewBox answers instead.
const LENGTH = /^\s*(-?[0-9]*\.?[0-9]+)\s*(px|pt|pc|mm|cm|in)?\s*$/i;
const PER_UNIT = { px: 1, pt: 96 / 72, pc: 16, mm: 96 / 25.4, cm: 96 / 2.54, in: 96 };

const toPixels = (value) => {
  const match = LENGTH.exec(String(value || ""));
  if (!match) return 0;
  const size = Number(match[1]) * (PER_UNIT[String(match[2] || "px").toLowerCase()] || 1);
  return Number.isFinite(size) && size > 1 ? size : 0;
};

// The markup of an SVG carried in a data: URL. It is only ever read - never
// inserted into a document - so a script inside it can't run. The picture
// itself is still drawn through <img>, which can't run scripts either.
export function decodeDataUrl(src = "") {
  const text = String(src || "");
  if (!/^data:image\/svg\+xml/i.test(text)) return "";
  const comma = text.indexOf(",");
  if (comma < 0) return "";
  const payload = text.slice(comma + 1);
  try {
    if (/;base64/i.test(text.slice(0, comma))) {
      return typeof atob === "function" ? atob(payload) : "";
    }
    return decodeURIComponent(payload);
  } catch (e) {
    return "";
  }
}

// The size an SVG says it is: its width and height, else its viewBox.
// `null` when the source isn't readable here (an https link, say) - the
// browser's own reading is used then.
export function readVectorSize(source = "") {
  const markup = /^data:/i.test(source) ? decodeDataUrl(source) : String(source || "");
  const tag = openingTag(markup);
  if (!tag) return null;
  const width = toPixels(attribute(tag, "width"));
  const height = toPixels(attribute(tag, "height"));
  if (width && height) return { width, height };
  const box = attribute(tag, "viewBox").trim().split(/[\s,]+/).map(Number);
  if (box.length === 4 && box.every((n) => Number.isFinite(n)) && box[2] > 0 && box[3] > 0) {
    return { width: box[2], height: box[3] };
  }
  return null;
}

// The size to draw a picture at 1:1, from whatever is known about it.
export function intrinsicSize({ src = "", markup = "", naturalWidth = 0, naturalHeight = 0, vector = false }) {
  if (vector) {
    const said = readVectorSize(markup || src);
    if (said) return said;
    // A vector the browser could measure (it carried width and height): its
    // reading is as good as ours. 300x150 is what a browser invents for an
    // SVG that says nothing, so it is not trusted as a size.
    if (naturalWidth > 1 && naturalHeight > 1 && !(naturalWidth === 300 && naturalHeight === 150)) {
      return { width: naturalWidth, height: naturalHeight };
    }
    return { ...DEFAULT_VECTOR_SIZE };
  }
  if (naturalWidth > 0 && naturalHeight > 0) return { width: naturalWidth, height: naturalHeight };
  return null;
}

// Where the picture sits so it fills the canvas, centred, with a little air
// around it. `null` when the canvas hasn't been laid out yet, so the caller
// leaves the view as it is rather than jumping to nonsense.
export function fitView({ boxWidth, boxHeight, width, height, vector = false, pad = 28 }) {
  if (!(boxWidth > 0) || !(boxHeight > 0) || !(width > 0) || !(height > 0)) return null;
  const room = { w: Math.max(1, boxWidth - pad * 2), h: Math.max(1, boxHeight - pad * 2) };
  const raw = Math.min(room.w / width, room.h / height);
  const k = clampScale(vector ? raw : Math.min(raw, RASTER_FIT_MAX));
  return { k, x: (boxWidth - width * k) / 2, y: (boxHeight - height * k) / 2 };
}

// Zooming keeps the point under the cursor (or the middle of the canvas)
// where it is, so the reader zooms into what they are looking at.
export function zoomView(view, factor, cx, cy) {
  const k = clampScale(view.k * factor);
  const ratio = k / view.k;
  return { k, x: cx - (cx - view.x) * ratio, y: cy - (cy - view.y) * ratio };
}

export const isVectorSource = (src = "") =>
  /^data:image\/svg\+xml/i.test(src) || /\.svgz?($|[?#])/i.test(String(src || ""));
